import React from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Stack,
  Tag,
  Text,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { Controller, UseFormMethods, useWatch } from 'react-hook-form';

import { axios } from 'src/lib/axios';
import {
  PASSIONS as CATEGORIES,
  skillOptions as tagOptions,
} from 'src/constants/passions-and-skills';
import { RestResponse } from 'src/types';

import AsyncMultipleSelect from 'components/multiple-select/async-multiple-select';
import MultipleSelect from 'components/multiple-select';

import { logger } from 'src/lib/logger';
import { getProfileFullName } from 'src/app/profile/utils';
import { ArticleFormValues, SIDE_MENU } from './types';

interface AdvancedSectionProps {
  section: string;
  control: UseFormMethods<ArticleFormValues>['control'];
}

type CoCreatorsResponse = RestResponse<
  {
    id: number;
    username: string;
    profileImgURL: string;
    firstName: string;
    lastName: string;
  }[]
>;

const MAX_CATEGORIES_SELECTED = 3;
const MAX_TAGS_SELECTED = 5;

function AdvancedSection({ section, control }: AdvancedSectionProps) {
  const watchedTags = useWatch<ArticleFormValues['tags']>({
    control,
    name: 'tags',
  });

  const creatorOptionsPromise = async (value) => {
    try {
      const response = await axios
        .get<CoCreatorsResponse>(`/cocreators/${value}`)
        .then((res) => res.data);

      if (response.code === 1000) {
        const users = response.data.map((user) => ({
          value: user.username,
          label:
            user.firstName && user.lastName
              ? getProfileFullName(user)
              : user.username,

          // TODO: In useForm to initial contributors with avatar will make browser crash...
          // label: (
          //   <Stack
          //     key={user.username}
          //     spacing={2}
          //     direction="row"
          //     alignItems="center"
          //   >
          //     <Avatar
          //       src={user.profileImgURL}
          //       name={`${user.firstName} ${user.lastName}`}
          //       width="25px"
          //       height="25px"
          //     />
          //     {user.firstName && user.lastName ? (
          //       <Text as="span">{`${user.firstName} ${user.lastName}`}</Text>
          //     ) : (
          //       <Text as="span">{`${user.username}`}</Text>
          //     )}
          //   </Stack>
          // ),
        }));

        return users;
      }

      return [];
    } catch (err) {
      logger.error(err);
    }
  };

  return (
    <Stack
      display={section === SIDE_MENU.ADVANCED ? 'flex' : 'none'}
      spacing={5}
      direction="column"
      width="100%"
    >
      {/* Project Contributors */}
      <FormControl id="contributors">
        <FormLabel color="gray.700">
          <Text as="b">Add Co-creators and contributors</Text>
        </FormLabel>
        <Text as="b" color="gray.500" fontSize="sm">
          Who helped you on this project?
        </Text>
        <Box mt={3}>
          <Controller
            name="contributors"
            control={control}
            render={({ onChange, value }) => (
              <AsyncMultipleSelect
                cacheOptions
                placeholder="Select Creators"
                isClearable={false}
                loadOptions={creatorOptionsPromise}
                value={value}
                onChange={onChange}
              />
            )}
          />
        </Box>
      </FormControl>

      {/* Project Categories */}
      <FormControl id="categories">
        <FormLabel color="gray.700">
          <Stack direction="row" spacing={2}>
            <Text as="b">Categorize This Project</Text>
            <Text fontSize="xs">choose up to 3</Text>
          </Stack>
        </FormLabel>
        <Text as="b" color="gray.500" fontSize="sm">
          Setup your project category can help other people find your works more
          easier.
        </Text>
        <Controller
          name="categories"
          control={control}
          rules={{
            validate: (v) => {
              return Array.isArray(v) && v.length <= MAX_CATEGORIES_SELECTED;
            },
          }}
          render={({ value, onChange }) => (
            <TagSelect value={value} onChange={onChange} options={CATEGORIES} />
          )}
        />
      </FormControl>

      {/* Project Tags */}
      <FormControl id="tags">
        <FormLabel color="gray.700">
          <Stack direction="row" spacing={2}>
            <Text as="b">Leave Tags for Project</Text>
            <Text fontSize="xs">choose up to 5</Text>
          </Stack>
        </FormLabel>
        <Text as="b" color="gray.500" fontSize="sm">
          Accurately defining your project tag can help you increase the
          exposure of your work much further.
        </Text>
        <Box mt={3}>
          <Controller
            name="tags"
            control={control}
            render={({ onChange, value }) => (
              <MultipleSelect
                placeholder="Select Tags"
                isClearable={false}
                value={value}
                options={
                  watchedTags?.length === MAX_TAGS_SELECTED ? [] : tagOptions
                }
                onChange={onChange}
              />
            )}
          />
        </Box>
      </FormControl>

      {/* TODO: Community not yet ready */}
      {/* <FormControl id="communities">
        <FormLabel color="gray.700">
          <Text as="b">Share to Communities</Text>
        </FormLabel>

        <Wrap spacing={2}>
          {['A', 'B', 'C'].map(community => (
            <Controller
              name="communities"
              key={community}
              control={control}
              defaultValue={false}
              render={({ onChange }) => (
                <Checkbox
                  as="b"
                  color="gray.500"
                  fontSize="sm"
                  isChecked
                  onChange={e => onChange(e.target.checked)}
                >
                  {community} Community
                </Checkbox>
              )}
            />
          ))}
        </Wrap>
      </FormControl> */}
    </Stack>
  );
}

function TagSelect({
  options = [],
  value = [],
  onChange,
}: {
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
}) {
  return (
    <Wrap mt={3}>
      {options.map((option) => {
        const isSelected = value.includes(option);
        return (
          <WrapItem key={option}>
            <Tag
              bg="white"
              size="md"
              border="1px solid"
              borderColor={isSelected ? 'blue.500' : 'gray.200'}
              color={isSelected ? 'blue.500' : 'gray.500'}
              px={3}
              py="12px"
              cursor="pointer"
              onClick={() => {
                if (isSelected) {
                  onChange(value.filter((v) => v !== option));
                } else if (value.length < MAX_CATEGORIES_SELECTED) {
                  onChange([...value, option]);
                }
              }}
            >
              {option}
            </Tag>
          </WrapItem>
        );
      })}
    </Wrap>
  );
}

export default AdvancedSection;
